import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { motion } from 'framer-motion';
import { Sneaker, GoogleLogo, CircleNotch } from '@phosphor-icons/react';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      navigate(data.role === 'admin' ? '/admin' : '/');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 bg-[#0A0A0A] items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src="https://images.unsplash.com/photo-1618153478389-b2ed8de18ed3?w=1200" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 p-12">
          <Sneaker size={48} weight="duotone" className="text-[#FF3B30] mb-6" />
          <h2 className="font-heading font-black text-4xl tracking-tighter uppercase text-white leading-tight">Find Your<br /><span className="text-[#FF3B30]">Perfect</span> Fit</h2>
          <p className="text-[#A1A1AA] mt-4 max-w-sm">AI-powered shoe recommendations matched to stores across Australia, UK, and US.</p>
        </div>
      </div>
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <motion.div className="w-full max-w-md" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/" className="flex items-center gap-2 mb-8">
            <Sneaker size={28} weight="duotone" className="text-[#FF3B30]" />
            <span className="font-heading font-black text-xl tracking-tighter uppercase">SOLEFINDER</span>
          </Link>
          <h1 className="font-heading font-black text-3xl tracking-tighter uppercase mb-2" data-testid="login-title">Sign In</h1>
          <p className="text-[#52525B] mb-6">Welcome back. Sign in to access your wishlist and recommendations.</p>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-4 text-sm" data-testid="login-error">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-4 py-3 focus:border-[#0A0A0A] focus:ring-0 transition-colors outline-none"
                data-testid="login-email-input" placeholder="you@example.com" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-4 py-3 focus:border-[#0A0A0A] focus:ring-0 transition-colors outline-none"
                data-testid="login-password-input" placeholder="Your password" />
            </div>
            <button type="submit" disabled={loading}
              className="w-full bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#D62828] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="login-submit-btn">
              {loading ? <CircleNotch size={20} className="animate-spin" /> : 'Sign In'}
            </button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-[#E4E4E7]" />
            <span className="text-xs text-[#52525B] uppercase font-bold">or</span>
            <div className="flex-1 h-px bg-[#E4E4E7]" />
          </div>

          <button onClick={loginWithGoogle}
            className="w-full border-2 border-[#0A0A0A] font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#F4F4F5] transition-colors flex items-center justify-center gap-2 text-sm"
            data-testid="google-login-btn">
            <GoogleLogo size={20} weight="bold" /> Continue with Google
          </button>

          <p className="mt-6 text-center text-sm text-[#52525B]">
            Don't have an account? <Link to="/register" className="text-[#FF3B30] font-bold hover:underline" data-testid="register-link">Sign Up</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}

export function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(email, password, name);
      navigate('/');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 py-12" data-testid="register-page">
      <motion.div className="w-full max-w-md" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Link to="/" className="flex items-center gap-2 mb-8">
          <Sneaker size={28} weight="duotone" className="text-[#FF3B30]" />
          <span className="font-heading font-black text-xl tracking-tighter uppercase">SOLEFINDER</span>
        </Link>
        <h1 className="font-heading font-black text-3xl tracking-tighter uppercase mb-2" data-testid="register-title">Create Account</h1>
        <p className="text-[#52525B] mb-6">Join SoleFinder to save your favourites and get personalised recommendations.</p>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-4 text-sm" data-testid="register-error">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required
              className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-4 py-3 focus:border-[#0A0A0A] focus:ring-0 transition-colors outline-none"
              data-testid="register-name-input" placeholder="Your name" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-4 py-3 focus:border-[#0A0A0A] focus:ring-0 transition-colors outline-none"
              data-testid="register-email-input" placeholder="you@example.com" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
              className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-4 py-3 focus:border-[#0A0A0A] focus:ring-0 transition-colors outline-none"
              data-testid="register-password-input" placeholder="Min 6 characters" />
          </div>
          <button type="submit" disabled={loading}
            className="w-full bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#D62828] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="register-submit-btn">
            {loading ? <CircleNotch size={20} className="animate-spin" /> : 'Create Account'}
          </button>
        </form>

        <div className="my-6 flex items-center gap-4">
          <div className="flex-1 h-px bg-[#E4E4E7]" />
          <span className="text-xs text-[#52525B] uppercase font-bold">or</span>
          <div className="flex-1 h-px bg-[#E4E4E7]" />
        </div>

        <button onClick={loginWithGoogle}
          className="w-full border-2 border-[#0A0A0A] font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#F4F4F5] transition-colors flex items-center justify-center gap-2 text-sm"
          data-testid="google-register-btn">
          <GoogleLogo size={20} weight="bold" /> Continue with Google
        </button>

        <p className="mt-6 text-center text-sm text-[#52525B]">
          Already have an account? <Link to="/login" className="text-[#FF3B30] font-bold hover:underline" data-testid="login-link">Sign In</Link>
        </p>
      </motion.div>
    </div>
  );
}

export function AuthCallback() {
  const hasProcessed = useRef(false);
  const { processSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const sessionId = hash?.split('session_id=')[1];
    if (!sessionId) {
      navigate('/login');
      return;
    }

    processSession(sessionId)
      .then((user) => navigate(user?.role === 'admin' ? '/admin' : '/', { state: { user } }))
      .catch(() => navigate('/login'));
  }, [location.hash, processSession, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback">
      <CircleNotch size={48} className="animate-spin text-[#FF3B30]" />
    </div>
  );
}
